/**
 * Storage abstraction: OS keychain on native, `localStorage` in the browser.
 *
 * This is the seam that lets one codebase hold a durable credential appropriately on
 * each platform. In a Tauri shell the refresh token goes into the platform store, which
 * on Windows is DPAPI-backed and on iOS/Android is the keychain. In a browser there is
 * no better option than `localStorage`, and pretending otherwise would be worse than
 * being explicit about it.
 */

import { isTauri } from "./runtime";

interface SecureStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

// localStorage is synchronous, but the interface is async because the native store
// is not. These wrap rather than await.
const browserStorage: SecureStorage = {
  get(key) {
    try {
      return Promise.resolve(window.localStorage.getItem(key));
    } catch {
      // Private browsing can throw on access rather than returning null.
      return Promise.resolve(null);
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* storage unavailable: the session simply will not survive a reload */
    }
    return Promise.resolve();
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* nothing to do */
    }
    return Promise.resolve();
  },
};

let nativeStore: unknown = null;

async function loadNativeStore(): Promise<unknown> {
  if (nativeStore) return nativeStore;
  const { load } = await import("@tauri-apps/plugin-store");
  nativeStore = await load("quantlab.secure.json", { autoSave: true });
  return nativeStore;
}

const tauriStorage: SecureStorage = {
  async get(key) {
    try {
      const store = (await loadNativeStore()) as { get: (k: string) => Promise<unknown> };
      const value = await store.get(key);
      return typeof value === "string" ? value : null;
    } catch {
      return browserStorage.get(key);
    }
  },
  async set(key, value) {
    try {
      const store = (await loadNativeStore()) as {
        set: (k: string, v: unknown) => Promise<void>;
      };
      await store.set(key, value);
    } catch {
      await browserStorage.set(key, value);
    }
  },
  async remove(key) {
    try {
      const store = (await loadNativeStore()) as { delete: (k: string) => Promise<void> };
      await store.delete(key);
    } catch {
      await browserStorage.remove(key);
    }
  },
};

export const secureStorage: SecureStorage = isTauri() ? tauriStorage : browserStorage;
